import type {RefObject} from 'react';
import {useCallback, useEffect, useState} from 'react';

const DEFAULT_MIN_HEIGHT = 200;

/**
 * Custom hook that manages the positioning and height of a dropdown list (or similar elements).
 * It determines if the dropdown should open upwards (`isTop`) and calculates the appropriate
 * height for the dropdown list (`listHeight`). This ensures that the dropdown fits within the viewport.
 * The height and positioning are recalculated every time the select element is clicked.
 *
 * @param selectRef A reference to the dropdown container element. This is used to calculate the position and size of the dropdown list based on the current viewport.
 * @param isOpen    A boolean indicating whether the dropdown is currently open. The calculations are triggered when the dropdown is opened.
 * @param minHeight Optional. The minimum height for the dropdown list. Defaults to a pre-defined value (`DEFAULT_MIN_HEIGHT`).
 * @returns An object containing the current state of the dropdown:
 * - `isTop`: A boolean indicating if the dropdown should open upwards.
 * - `listHeight`: The calculated height of the dropdown list.
 *
 * @example
 * ```tsx
 * const selectRef = useRef<HTMLDivElement>(null);
 * const {isTop, listHeight} = useSelectList(selectRef, 200);
 * ```
 */
export const useSelectList = (
    selectRef: RefObject<HTMLDivElement | HTMLLabelElement | null>,
    isOpen: boolean,
    minHeight = DEFAULT_MIN_HEIGHT
) => {
    const [state, dispatch] = useState({
        isTop: false,
        listHeight: 0
    });

    const handleClick = useCallback(() => {
        if (!isOpen) {
            dispatch(calculateList(selectRef, minHeight));
        }
    }, [selectRef, minHeight, isOpen]);

    useEffect(() => {
        const select = selectRef.current;

        select?.addEventListener('click', handleClick);

        return () => {
            select?.removeEventListener('click', handleClick);
        };
    }, [selectRef, handleClick]);

    return state;
};

/**
 * Calculates the height of a list based on the provided parameters.
 * The height is determined based on the position and dimensions of the selectRef element and its overflow parent.
 * If the minHeight parameter is specified, it is used as a minimum height constraint.
 * The calculated height takes into account a safeSpace of 50 pixels.
 *
 * @param selectRef A ref to the HTMLDivElement representing the select element.
 * @param minHeight The minimum height constraint for the list.
 * @returns An object containing the calculated list height and a boolean indicating if the list is positioned at the top.
 *
 * @example
 * ```ts
 * const selectRef = useRef<HTMLDivElement>(null);
 * const minHeight = 100;
 * const {isTop, listHeight} = calculateList(selectRef, minHeight);
 * ```
 */
const calculateList = (selectRef: RefObject<HTMLDivElement | HTMLLabelElement | null>, minHeight: number) => {
    let isTop = false;
    let listHeight = 0;
    const safeSpace = 50;

    if (selectRef.current) {
        const {height, top: parentTop} = getFirstOverflowParent(selectRef.current).getBoundingClientRect();
        const {top: selectTop} = selectRef.current.getBoundingClientRect();
        const top = selectTop - parentTop;
        const bottom = height - top - selectRef.current.offsetHeight;

        if (bottom > minHeight) {
            listHeight = bottom - safeSpace;
            isTop = false;
        } else if (top > minHeight) {
            listHeight = top - safeSpace;
            isTop = true;
        } else {
            listHeight = (bottom > top) ? bottom - safeSpace : top - safeSpace;
            isTop = (bottom < top);
        }
    }

    return {
        isTop,
        listHeight
    };
};

/**
 * Retrieves the first overflow parent of the given element, or the document body if no overflow parent is found.
 * An overflow parent is an ancestor element that has its overflow property set to a value other than 'visible' or 'hidden'.
 * If the given element is null or the document body, the function returns the document body.
 *
 * @param element The element for which to find the first overflow parent.
 * @returns The first overflow parent element or the document body if no overflow parent is found.
 *
 * @example
 * ```ts
 * const element = document.getElementById('myElement');
 * const firstOverflowParent = getFirstOverflowParent(element);
 * ```
 */
const getFirstOverflowParent = (element?: HTMLElement | null): HTMLElement => {
    if (!element || element === document.body) {
        return document.body;
    }

    const {overflow, overflowY} = getComputedStyle(element, null);

    if (overflow !== 'visible' || overflowY !== 'visible') {
        return element;
    }

    return getFirstOverflowParent(element.parentElement);
};